// ContactImportModal — bulk contact import for DropPin.
//
// v0.39.2 — initial. Three paths:
//   1. vCard (.vcf) file upload  — universal. iPhone Contacts "Share contact"
//                                   and Google Contacts vCard export both
//                                   produce this. Works on every device.
//   2. CSV file upload            — for bulk subbie rosters that started
//                                   as a spreadsheet.
//   3. navigator.contacts.select() — opportunistic. Android Chrome only.
//                                   Native OS picker, multi-select.
//                                   The button hides on browsers without
//                                   the API.
//
// Flow: pick a source -> parse -> show preview list with name/email/phone/
// trade per row -> click Import All -> POST each to /api/contacts in
// sequence. Errors per row are reported inline; the import doesn't stop
// for a single failure (best-effort).
//
// Free-tier cap is honoured at submit-time: if there's space for N more
// contacts and the parsed list has > N, only the first N go through;
// the user is told and an UpgradePrompt is surfaced through the parent.
//
// v0.295.0 (2026-11-01) — a fourth path: 4. From InSpec. Brett, on this box:
// "Do we not have import contacts from inspec?" — "yes do the import feature".
// The card opens ImportFromInSpecModal (its own list, ticks and server-side
// import; see that file). The card is hidden only when the signed-in payload
// LISTS the company's products and InSpec is not among them; anything less
// certain shows it, and the server is the gate (no InSpec / no seat say so).

import React, { useState } from 'react';
import { DownloadCloud } from 'lucide-react';
import { apiSend } from '../api';
import { theme, card, btnPrimary, btnSecondary, input } from '../theme';
import { parseVCard } from '../utils/vcard-parser';
import { parseCsv } from '../utils/csv-parser';
import { tradeLabel, tradeColor } from '../utils/trades';
import { getCurrentUser } from '../utils/user';
import ImportFromInSpecModal from './ImportFromInSpecModal';

const SUPPORTS_NATIVE_PICKER =
  typeof navigator !== 'undefined' &&
  navigator.contacts &&
  typeof navigator.contacts.select === 'function';

// One width for every source's icon, so the card titles start level.
const sourceIcon = { display: 'inline-flex', width: 30, flexShrink: 0, justifyContent: 'center' };

// v0.295.0 — true only when the cached sign-in names the company's products
// and none is InSpec. No user, no tenants, or tenants with no product list =
// not known, so the card shows and the server answers.
export function inspecKnownAbsent(user) {
  const tenants = user && Array.isArray(user.tenants) ? user.tenants : null;
  if (!tenants || tenants.length === 0) return false;
  let listed = false;
  for (const t of tenants) {
    const products = t && Array.isArray(t.products) ? t.products : [];
    if (products.length) listed = true;
    if (products.some((p) => p && String(p.code || '').toLowerCase() === 'inspec')) return false;
  }
  return listed;
}

export default function ContactImportModal({ onCancel, onImported, existingCount, freeCap }) {
  const [inspecOpen, setInspecOpen] = useState(false);
  const [parsed, setParsed] = useState([]);     // [{name, email, phone, company, trade, notes}]
  const [source, setSource] = useState(null);    // 'vcard' | 'csv' | 'native'
  const [parseErr, setParseErr] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, ok: 0, fail: 0 });
  const [finished, setFinished] = useState(false);

  // How many contacts the user is allowed to add right now.
  // freeCap === Infinity means Pro tier, no limit.
  const remaining = Math.max(0, (freeCap || Infinity) - (existingCount || 0));
  const willImport = parsed.slice(0, remaining);
  const willTruncate = parsed.length > willImport.length;

  async function handleFile(file) {
    setParseErr(null);
    if (!file) return;
    const text = await file.text();
    const isVcf = /\.vcf$/i.test(file.name) || /^BEGIN:VCARD/i.test(text.trim());
    let list = [];
    try {
      list = isVcf ? parseVCard(text) : parseCsv(text);
    } catch (e) {
      setParseErr('Could not read the file: ' + (e?.message || String(e)));
      return;
    }
    if (!list.length) {
      setParseErr(
        isVcf
          ? 'No contacts found in this .vcf file.'
          : 'No contacts found in this CSV. The first row should be headers (Name, Email, Phone, Company, Trade, Notes).'
      );
      return;
    }
    setParsed(list);
    setSource(isVcf ? 'vcard' : 'csv');
  }

  async function handleNativePicker() {
    setParseErr(null);
    if (!SUPPORTS_NATIVE_PICKER) {
      setParseErr('Your browser doesn’t support the native contact picker. Try the vCard or CSV path instead.');
      return;
    }
    try {
      const props = ['name', 'email', 'tel'];
      const picked = await navigator.contacts.select(props, { multiple: true });
      if (!picked || !picked.length) return;
      const list = picked.map(c => ({
        name:    Array.isArray(c.name)  ? (c.name[0]  || '') : (c.name  || ''),
        email:   Array.isArray(c.email) ? (c.email[0] || null) : (c.email || null),
        phone:   Array.isArray(c.tel)   ? (c.tel[0]   || null) : (c.tel   || null),
        company: null,
        trade:   null,
        notes:   null,
      })).filter(c => c.name);
      setParsed(list);
      setSource('native');
    } catch (e) {
      setParseErr(e?.message || String(e));
    }
  }

  async function runImport() {
    setImporting(true);
    setProgress({ done: 0, ok: 0, fail: 0 });
    const created = [];
    let ok = 0, fail = 0;
    for (let i = 0; i < willImport.length; i++) {
      const c = willImport[i];
      try {
        const row = await apiSend('POST', '/api/contacts', c);
        created.push(row);
        ok++;
      } catch (_e) {
        fail++;
      }
      setProgress({ done: i + 1, ok, fail });
    }
    setImporting(false);
    setFinished(true);
    if (typeof onImported === 'function') {
      onImported({ created, ok, fail, truncated: willTruncate });
    }
  }

  if (inspecOpen) {
    return (
      <ImportFromInSpecModal
        onBack={() => setInspecOpen(false)}
        onClose={onCancel}
        onImported={(c) => onImported && onImported({ truncated: !!(c && c.truncated) })}
      />
    );
  }

  return (
    <div
      onClick={() => !importing && onCancel()}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface, width: 640, maxWidth: '100%', maxHeight: '85vh',
          borderRadius: 12, padding: 22, display: 'flex', flexDirection: 'column',
          boxShadow: '0 12px 32px rgba(15,23,42,0.25)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>Import contacts</h3>
          <button
            type="button"
            onClick={onCancel}
            disabled={importing}
            style={{
              border: 0, background: 'transparent', cursor: importing ? 'default' : 'pointer',
              fontSize: 20, color: theme.textLight, lineHeight: 1,
            }}
          >×</button>
        </div>

        {/* SOURCE PICKER --------------------------------------------------- */}
        {parsed.length === 0 && (
          <div>
            <div style={{ fontSize: 13, color: theme.textLight, marginBottom: 12 }}>
              Pick a source. iPhone: <em>Contacts → Share contact → Save to Files</em> as .vcf.
              Google Contacts: <em>Export → vCard</em>. Spreadsheet: save as CSV with headers.
            </div>

            <label style={{ ...card, padding: 14, display: 'block', cursor: 'pointer', marginBottom: 8 }}>
              <input
                type="file"
                accept=".vcf,.vcard,.csv,.txt,text/*"
                onChange={(e) => handleFile(e.target.files?.[0])}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ ...sourceIcon, fontSize: 22 }} aria-hidden="true">{'\u{1F4C7}'}</span>
                <span>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>Upload a file</div>
                  <div style={{ fontSize: 12, color: theme.textLight }}>vCard (.vcf) or CSV. Multi-contact files are fine.</div>
                </span>
              </div>
            </label>

            {!inspecKnownAbsent(getCurrentUser()) && (
              <button
                type="button"
                data-source="inspec"
                onClick={() => setInspecOpen(true)}
                style={{ ...card, padding: 14, display: 'block', width: '100%', cursor: 'pointer', textAlign: 'left', border: `1px solid ${theme.border}`, marginBottom: 8, color: theme.text, font: 'inherit' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ ...sourceIcon, color: theme.brand }} aria-hidden="true">
                    <DownloadCloud size={22} />
                  </span>
                  <span>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>From InSpec</div>
                    <div style={{ fontSize: 12, color: theme.textLight }}>Your company’s InSpec people — pick who to bring in.</div>
                  </span>
                </div>
              </button>
            )}

            {SUPPORTS_NATIVE_PICKER && (
              <button
                type="button"
                onClick={handleNativePicker}
                style={{ ...card, padding: 14, display: 'block', width: '100%', cursor: 'pointer', textAlign: 'left', border: `1px solid ${theme.border}`, color: theme.text, font: 'inherit' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ ...sourceIcon, fontSize: 22 }} aria-hidden="true">{'\u{1F4F1}'}</span>
                  <span>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>Pick from this device</div>
                    <div style={{ fontSize: 12, color: theme.textLight }}>Opens your phone’s native contact picker (Android Chrome only).</div>
                  </span>
                </div>
              </button>
            )}

            {parseErr && (
              <div style={{ marginTop: 10, padding: '8px 10px', background: theme.bg, border: `1px solid ${theme.danger}`, color: theme.danger, borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                {parseErr}
              </div>
            )}
          </div>
        )}

        {/* PREVIEW + COMMIT ------------------------------------------------ */}
        {parsed.length > 0 && !finished && (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div style={{ fontSize: 13, color: theme.textLight, marginBottom: 8 }}>
              <strong>{parsed.length}</strong> contact{parsed.length === 1 ? '' : 's'} parsed from {source === 'vcard' ? 'vCard' : source === 'csv' ? 'CSV' : 'your device'}.
              {willTruncate && (
                <span style={{ color: theme.danger, fontWeight: 600 }}>
                  {' '}Only {willImport.length} will land (free-tier cap). Upgrade to Pro for unlimited.
                </span>
              )}
            </div>

            <div style={{ overflowY: 'auto', flex: 1, border: `1px solid ${theme.border}`, borderRadius: 6, marginBottom: 10 }}>
              {parsed.map((c, i) => {
                const skipped = i >= willImport.length;
                return (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'auto 1.4fr 1.6fr 1.2fr 1.2fr',
                      gap: 8,
                      padding: '6px 10px',
                      borderBottom: i < parsed.length - 1 ? `1px solid ${theme.border}` : 'none',
                      fontSize: 12,
                      opacity: skipped ? 0.4 : 1,
                      background: skipped ? theme.bg : 'transparent',
                    }}
                  >
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: c.trade ? tradeColor(c.trade) : theme.textLight,
                      alignSelf: 'center', display: 'inline-block',
                    }} title={c.trade ? tradeLabel(c.trade) : 'No trade'} />
                    <span style={{ fontWeight: 600, color: theme.text }}>{c.name}</span>
                    <span style={{ color: theme.textLight }}>{c.email || ''}</span>
                    <span style={{ color: theme.textLight }}>{c.phone || ''}</span>
                    <span style={{ color: theme.textLight }}>{c.company || ''}</span>
                  </div>
                );
              })}
            </div>

            {importing && (
              <div style={{ fontSize: 12, color: theme.textLight, marginBottom: 8 }}>
                Importing… {progress.done} / {willImport.length}
                {progress.fail > 0 && (<span style={{ color: theme.danger }}> ({progress.fail} failed)</span>)}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => { setParsed([]); setSource(null); }} disabled={importing} style={btnSecondary}>
                Pick a different file
              </button>
              <button type="button" onClick={onCancel} disabled={importing} style={btnSecondary}>
                Cancel
              </button>
              <button
                type="button"
                onClick={runImport}
                disabled={importing || willImport.length === 0}
                style={{ ...btnPrimary, opacity: importing ? 0.6 : 1 }}
              >
                {importing ? 'Importing…' : `Import ${willImport.length}`}
              </button>
            </div>
          </div>
        )}

        {/* DONE ------------------------------------------------------------ */}
        {finished && (
          <div>
            <div style={{
              padding: 14, marginBottom: 12,
              background: theme.bg, border: `1px solid ${theme.success}`, borderRadius: 8,
              fontSize: 13, color: theme.success,
            }}>
              <strong>Imported {progress.ok} of {willImport.length}.</strong>
              {progress.fail > 0 && <> {progress.fail} row{progress.fail === 1 ? '' : 's'} couldn't be saved.</>}
              {willTruncate && <> {parsed.length - willImport.length} skipped (free-tier cap).</>}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={onCancel} style={btnPrimary}>Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
